/* Added-to-cart notice (Clarity audit 2026-10-15, discussion 01).
 *
 * With cart_action "no-overlay", every Add to cart sent the shopper to /cart:
 * product pages via component-product-form.js (cartType 'page' -> fetch
 * /cart/add.js, then location = /cart) and grid quick-buys via
 * window.handleAddToCart (doNext 'page'). On mobile, 68% of those landings
 * went straight back to browsing. Seth 2026-10-20: add in the background and
 * show a notice instead, on product pages too; notice at the top. The cart
 * page and cart_action stay as they are. The free-shipping gap was dropped
 * from the notice (Seth 2026-10-20): one variable in the before/after, and
 * /cart's own bar already shows it.
 *
 * How: one document-level capture-phase submit listener. It runs before
 * Combine's form-level submit handlers, so stopPropagation keeps them from
 * redirecting, and no native Combine file is edited. Combine's own hooks were
 * rejected: cartType 'theme' + KROWN.refreshCart needs data-ajax-cart (Liquid,
 * gated on overlay) and cart_mini_enabled covers grid adds only.
 *
 * Load order matters: theme.liquid includes this AFTER ml-preorder-sync.js,
 * whose capture-phase submit listener must stamp the Estimated Ship property
 * before the form is read. The form is also read after a microtask, so a
 * same-phase listener registered later still runs first.
 *
 * Deliberately NOT intercepted (they keep their own jump to /cart):
 * - anything on the cart page (cart quick-view adds rely on refreshCart);
 * - forms with a required custom property (Combine validates those);
 * - Pay It Forward adds (product-custom.js) and Easy Bundles adds
 *   (ml-bundle-eb.js), which are their own click paths;
 * - Adoric bundle widgets, which never submit a /cart/add form.
 *
 * Events: the /cart/add.js request is byte-for-byte what Combine sent
 * (same body serialisation, same headers), so the platform add-to-cart
 * signals that key on it are unchanged. Shopify's CartLinesUpdateEvent is
 * dispatched only for grid quick-buys, mirroring handleAddToCart; Combine's
 * product-page 'page' path never dispatched it, and adding it there could
 * double count. */
(function () {
  if (window.__mlCartNotice) return;
  window.__mlCartNotice = true;

  // Long enough to read the title and reach a button; paused while the
  // notice is hovered or holds focus (WCAG 2.2.1).
  var HIDE_MS = 5000;
  var ERROR_TEXT = 'Something went wrong adding this to your cart. Please try again.';

  function settings() {
    return (window.KROWN && window.KROWN.settings) || {};
  }

  function cartUrl() {
    return (settings().routes && settings().routes.cart_url) || '/cart';
  }

  function cartAddUrl() {
    return ((settings().routes && settings().routes.cart_add_url) || '/cart/add') + '.js';
  }

  function onCartPage() {
    return document.body && /(^|\s)template-cart(\s|$)/.test(document.body.className);
  }

  // Which add-to-cart forms this owns, and through which Combine path they
  // used to go. Returns null for anything left alone.
  function classify(form) {
    if (!form || form.tagName !== 'FORM') return null;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return null;
    if (onCartPage()) return null;

    var quickBuy = form.parentElement && form.parentElement.closest('[data-js-quick-buy-form]');
    if (quickBuy) return { kind: 'quick-buy', host: quickBuy };

    var productForm = form.closest('product-form');
    if (!productForm) return null;
    var scope = productForm.closest('[data-js-product-component]') || productForm;
    if (scope.querySelector('[data-js-custom-property] [required]')) return null;
    return { kind: 'product-form', host: productForm };
  }

  function serialize(form) {
    var params = new URLSearchParams();
    Array.prototype.forEach.call(form.elements, function (field) {
      if (!field.name || field.disabled) return;
      if (['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      if (field.type === 'select-multiple') {
        Array.prototype.forEach.call(field.options, function (o) {
          if (o.selected) params.append(field.name, o.value);
        });
        return;
      }
      params.append(field.name, field.value);
    });
    return params.toString();
  }

  /* ---- notice element ---- */

  var notice = null;
  var hideTimer = null;
  var held = false;

  function build() {
    notice = document.createElement('div');
    notice.className = 'ml-cart-notice';
    // Announced through Combine's always-present #screen-reader-info region
    // (see announce); a live region that is itself hidden until shown is not
    // reliably read.
    notice.setAttribute('role', 'region');
    notice.setAttribute('aria-label', 'Added to cart');
    notice.hidden = true;
    notice.innerHTML =
      '<div class="ml-cart-notice__card">' +
        '<img class="ml-cart-notice__thumb" alt="" width="56" height="56">' +
        '<div class="ml-cart-notice__body">' +
          '<p class="ml-cart-notice__heading">Added to cart</p>' +
          '<p class="ml-cart-notice__title"></p>' +
        '</div>' +
        '<div class="ml-cart-notice__actions">' +
          '<a class="ml-cart-notice__view" href="' + cartUrl() + '">View cart</a>' +
          '<button type="button" class="ml-cart-notice__keep">Keep shopping</button>' +
        '</div>' +
      '</div>';
    notice.querySelector('.ml-cart-notice__keep').addEventListener('click', hide);
    notice.addEventListener('mouseenter', function () { held = true; clearTimeout(hideTimer); });
    notice.addEventListener('mouseleave', function () { held = false; schedule(); });
    notice.addEventListener('focusin', function () { held = true; clearTimeout(hideTimer); });
    notice.addEventListener('focusout', function (e) {
      if (notice.contains(e.relatedTarget)) return;
      held = false;
      schedule();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && notice && !notice.hidden) hide();
    });
    document.body.appendChild(notice);
  }

  function schedule() {
    clearTimeout(hideTimer);
    if (!held) hideTimer = setTimeout(hide, HIDE_MS);
  }

  function hide() {
    clearTimeout(hideTimer);
    held = false;
    if (!notice || notice.hidden) return;
    var hadFocus = notice.contains(document.activeElement);
    notice.classList.remove('is-open');
    notice.hidden = true;
    if (hadFocus && lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  }

  var lastTrigger = null;

  // Sit just under the header when it is on screen (sticky), so the cart icon
  // whose count just changed stays visible; otherwise at the top edge.
  function topOffset() {
    var header = document.querySelector('.site-header-landmark') || document.querySelector('header');
    var bottom = header ? header.getBoundingClientRect().bottom : 0;
    return Math.max(8, Math.min(bottom, window.innerHeight / 3) + 8);
  }

  function announce(text) {
    var region = document.getElementById('screen-reader-info');
    if (!region) return;
    region.textContent = text;
    setTimeout(function () { region.textContent = ''; }, 3000);
  }

  function show(item) {
    if (!notice) build();
    var thumb = notice.querySelector('.ml-cart-notice__thumb');
    var src = item && (item.featured_image && item.featured_image.url || item.image);
    if (src) {
      thumb.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=112';
      thumb.hidden = false;
    } else {
      thumb.removeAttribute('src');
      thumb.hidden = true;
    }
    var title = item ? (item.product_title || item.title || '') : '';
    if (item && item.variant_title) title += ', ' + item.variant_title;
    notice.querySelector('.ml-cart-notice__title').textContent = title;
    notice.style.top = topOffset() + 'px';
    notice.hidden = false;
    announce('Added to cart: ' + title + '.');
    // Next frame so the slide-in transition runs from the hidden state.
    requestAnimationFrame(function () { notice.classList.add('is-open'); });
    schedule();
  }

  /* ---- add flow ---- */

  function setCount(cart) {
    document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
    });
  }

  function showError(form, info, text) {
    var alert = document.createElement('span');
    alert.className = 'alert alert--error';
    alert.textContent = text;
    if (info.kind === 'product-form') {
      var old = document.getElementById('product-page-form-error-cart-alert');
      if (old) old.remove();
      alert.id = 'product-page-form-error-cart-alert';
      alert.style.marginTop = '1em';
      alert.style.marginBottom = '0';
      form.parentElement.append(alert);
    } else {
      form.querySelectorAll('.alert--error').forEach(function (a) { a.remove(); });
      form.prepend(alert);
    }
  }

  function dispatchEvents(form, info, body) {
    var params = new URLSearchParams(body);
    var variantId = params.get('id');
    var K = window.KROWN && window.KROWN.functions;
    if (info.kind === 'quick-buy' && K && typeof K.dispatchStandardEvent === 'function') {
      K.dispatchStandardEvent('CartLinesUpdateEvent', document.body, {
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: parseInt(params.get('quantity'), 10) || 1 }],
        promise: Promise.resolve({ cart: null })
      });
    }
    if (K && typeof K.eventDispatcher === 'function') {
      K.eventDispatcher('krown:cart:add', { productForm: form, variantId: variantId });
    }
  }

  function add(form, info, submitter) {
    var button = submitter || form.querySelector('[type="submit"]');
    if (button) button.classList.add('working');
    var body = serialize(form);
    lastTrigger = button;

    fetch(cartAddUrl(), {
      body: body,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      method: 'POST'
    })
      .then(function (r) { return r.json(); })
      .then(function (added) {
        if (added && added.status && added.status !== 200) {
          showError(form, info, typeof added.description === 'string' ? added.description : (added.message || ERROR_TEXT));
          return;
        }
        form.querySelectorAll('.alert--error').forEach(function (a) { a.remove(); });
        var old = document.getElementById('product-page-form-error-cart-alert');
        if (old) old.remove();

        dispatchEvents(form, info, body);

        // A quick-view modal closes itself on Combine's add-to-cart event; that
        // event is not dispatched here (QuickAddToCart redirects on it), so
        // close the modal directly.
        var modal = form.closest('modal-box');
        if (modal && typeof modal.hide === 'function') modal.hide();

        var item = added && added.items ? added.items[added.items.length - 1] : added;
        // The item is already in the cart: a fault from here on must never
        // reach the outer catch and tell the shopper the add failed.
        try { show(item); } catch (err) { console.warn('[ml-cart-notice]', err); }
        return fetch(cartUrl() + '.js')
          .then(function (r) { return r.json(); })
          .then(setCount)
          .catch(function () {});
      })
      .catch(function () {
        showError(form, info, ERROR_TEXT);
      })
      .finally(function () {
        delete form.dataset.mlAdding;
        if (button) button.classList.remove('working');
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var info = classify(form);
    if (!info) return;
    e.preventDefault();
    e.stopPropagation();
    // A second tap while the first add is in flight would add the item twice
    // (the old redirect hid this by leaving the page).
    if (form.dataset.mlAdding === '1') return;
    form.dataset.mlAdding = '1';
    var submitter = e.submitter;
    Promise.resolve().then(function () { add(form, info, submitter); });
  }, true);
})();
